<template>
  <section v-if="stops.length" class="journey-chapters">
    <header class="journey-chapters-head">
      <div>
        <h2 class="journey-chapters-title">{{ t('journeyChapters') }}</h2>
        <p class="journey-chapters-hint">{{ t('journeyChaptersHint') }}</p>
      </div>
      <div class="journey-chapters-tools">
        <button v-if="editable" type="button" class="journey-edit-stops" @click="$emit('edit')">
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
            <path fill="currentColor" d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25zm17.7-10.2a1 1 0 0 0 0-1.42l-2.33-2.33a1 1 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
          </svg>
          {{ t('journeyEditStops') }}
        </button>
        <div v-if="stops.length > 1" class="journey-arrows">
          <button type="button" class="journey-arrow" :aria-label="t('journeyPrevious')" @click="page(-1)">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path fill="currentColor" d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4L10.8 12z"/>
            </svg>
          </button>
          <button type="button" class="journey-arrow" :aria-label="t('journeyNext')" @click="page(1)">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path fill="currentColor" d="M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z"/>
            </svg>
          </button>
        </div>
      </div>
    </header>

    <ol ref="track" class="journey-track">
      <li
        v-for="(stop, index) in stops"
        :key="`${stop.firstDay}-${stop.accommodationId}`"
        class="journey-card"
        :style="{ animationDelay: `${Math.min(index, 8) * 80}ms` }"
      >
        <button
          type="button"
          class="journey-card-button"
          :aria-label="`${stop.name} · ${dayRangeLabel(stop)}`"
          @click="$emit('open-stop', stop)"
        >
          <img
            v-if="stop.photo && !brokenPhotos[stop.photo]"
            class="journey-card-photo"
            :src="$store.getters.getResizeImageForMobileDevices(stop.photo)"
            :alt="stop.name"
            loading="lazy"
            @error="markBroken(stop.photo)"
          >
          <span v-else class="journey-card-photo journey-card-empty" aria-hidden="true">
            {{ initial(stop.name) }}
          </span>
          <span class="journey-card-shade" aria-hidden="true"></span>
          <span class="journey-card-number" aria-hidden="true">{{ chapterNumber(index) }}</span>
          <span class="journey-card-text">
            <span class="journey-card-days">{{ dayRangeLabel(stop) }}</span>
            <span v-if="stop.name" class="journey-card-name">{{ stop.name }}</span>
          </span>
        </button>
      </li>
    </ol>
  </section>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";

/**
 * The journey design's stops as a gallery of tall photo cards, swiped
 * sideways (snapping card to card) on a phone and paged with arrows on a
 * desktop. Each card carries a big chapter number, its days and its name;
 * tapping one goes to its day in the trip plan.
 */
export default {
  name: 'JourneyChapters',
  mixins: [translationMixin],
  props: {
    // buildPosterStops output.
    stops: { type: Array, default: () => [] },
    // The ride manager's "edit the stops" button, which emits edit.
    editable: { type: Boolean, default: false },
  },
  data() {
    return { brokenPhotos: {} };
  },
  methods: {
    chapterNumber(index) {
      return String(index + 1).padStart(2, '0');
    },
    dayRangeLabel(stop) {
      if (stop.firstDay === stop.lastDay)
        return this.t('dayNumber', { number: stop.firstDay });
      return this.t('posterDayRange', { from: stop.firstDay, to: stop.lastDay });
    },
    initial(name) {
      return (name || '').trim().charAt(0).toUpperCase();
    },
    markBroken(photo) {
      this.$set(this.brokenPhotos, photo, true);
    },
    // One screenful of cards forward (1) or back (-1), in reading order: under
    // RTL "forward" is to the left.
    page(direction) {
      const track = this.$refs.track;
      if (!track) return;
      const rtl = getComputedStyle(track).direction === 'rtl';
      const distance = track.clientWidth * 0.85 * direction * (rtl ? -1 : 1);
      track.scrollBy({ left: distance, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.journey-chapters {
  --journey-card-width: clamp(220px, 68vw, 300px);
  --journey-gutter: clamp(16px, 5vw, 56px);

  max-width: 1120px;
  margin: 0 auto;
  padding: clamp(36px, 7vw, 72px) 0 clamp(20px, 4vw, 40px);
}

.journey-chapters-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 0 var(--journey-gutter);
  margin-bottom: 22px;
}

.journey-chapters-title {
  margin: 0;
  font-size: clamp(28px, 6vw, 44px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: #0d1117;
}

.journey-chapters-hint {
  margin: 8px 0 0;
  font-size: 15px;
  color: #6e6e73;
}

/* Beside the title while there is room, under it on a phone. */
.journey-chapters-head > div:first-child {
  flex: 1 1 280px;
}

.journey-chapters-tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.journey-edit-stops {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 46px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: rgba(7, 160, 195, 0.1);
  background: color-mix(in srgb, var(--primary-color, #07a0c3) 11%, #fff);
  color: var(--primary-color, #07a0c3);
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.journey-edit-stops:hover {
  filter: brightness(0.97);
}

.journey-arrows {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.journey-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 1px solid #e3e3e6;
  border-radius: 50%;
  background: #fff;
  color: #0d1117;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.journey-arrow:hover {
  background: var(--primary-background, #07a0c3);
  border-color: transparent;
  color: var(--primary-text-color, #fff);
}

/* A phone swipes the cards: the arrows only crowd the heading. A narrow
   window on a computer keeps them, as a mouse cannot swipe. */
@media (max-width: 600px) and (hover: none) {
  .journey-arrows {
    display: none;
  }
}

/* The arrows' glyphs point the reading way under RTL too. */
[dir="rtl"] .journey-arrow svg {
  transform: scaleX(-1);
}

.journey-track {
  display: flex;
  gap: 16px;
  margin: 0;
  padding: 6px var(--journey-gutter) 28px;
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 var(--journey-gutter);
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.journey-track::-webkit-scrollbar {
  display: none;
}

.journey-card {
  flex: 0 0 var(--journey-card-width);
  scroll-snap-align: start;
  animation: journey-card-in 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.journey-card-button {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  padding: 0;
  border: 0;
  border-radius: 24px;
  overflow: hidden;
  background: #1b1f27;
  color: #fff;
  text-align: start;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 22px 40px -24px rgba(0, 0, 0, 0.55);
  transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.35s ease;
}

.journey-card-button:hover,
.journey-card-button:focus-visible {
  transform: translateY(-6px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 32px 50px -24px rgba(0, 0, 0, 0.6);
}

.journey-card-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.journey-card-button:hover .journey-card-photo {
  transform: scale(1.07);
}

.journey-card-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 96px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  background:
    radial-gradient(120% 90% at 20% 0%, rgba(255, 255, 255, 0.25), transparent 60%),
    var(--primary-background, #07a0c3);
}

.journey-card-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(8, 12, 20, 0.88) 0%, rgba(8, 12, 20, 0.25) 45%, rgba(8, 12, 20, 0) 65%);
}

.journey-card-number {
  position: absolute;
  top: 14px;
  inset-inline-start: 18px;
  font-size: 54px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  color: transparent;
  -webkit-text-stroke: 1.5px rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.25);
}

.journey-card-text {
  position: absolute;
  inset-inline: 18px;
  bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.journey-card-days {
  align-self: flex-start;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.journey-card-name {
  font-size: 23px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes journey-card-in {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .journey-card {
    animation: none;
  }
  .journey-card-button,
  .journey-card-photo {
    transition: none;
  }
}
</style>
